import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import MessageBubble, { colorForId } from './MessageBubble';
import ParticipantsSidebar from './ParticipantsSidebar';
import RightPanel from './RightPanel';
import NotificationBell from './NotificationBell';
import SlashCommandMenu, { filterSlashCommands } from './SlashCommandMenu';
import SearchPanel from './SearchPanel';
import AIBadge from './AIBadge';
import Toast from './Toast';
import BackButton from './BackButton';
import {
  IconArtifact,
  IconBoard,
  IconCatchUp,
  IconChevronDown,
  IconCompact,
  IconDecision,
  IconJson,
  IconLogout,
  IconMarkdown,
  IconMessage,
  IconMore,
  IconObjective,
  IconPaperclip,
  IconPeople,
  IconSearch,
  IconSend,
  IconSparkle,
  IconTodo,
} from './Icons';
import { EditableIcon } from './EntityIcon';
import { BrandMark } from './BrandMark';
import { useFileUpload } from '../hooks/useFileUpload';
import './ChatThread.css';

// Word-boundary AI-mention test so "@Aimy" matches but "@Aimyish" doesn't.
function aiMentionRegex(name) {
  return new RegExp(`@${name.replace(/\s+/g, '')}(?![a-zA-Z])`, 'i');
}

function ChatThread({
  user,
  room,
  messages,
  participants,
  workspaceMembers,
  connected,
  error,
  artifacts,
  todos,
  decisions,
  shortLists,
  onSendMessage,
  onLeaveThread,
  onLogout,
  onInvokeAI,
  onAddArtifact,
  onRemoveArtifact,
  onUpdateArtifact,
  onGetArtifactVersions,
  onVotePoll,
  onClosePoll,
  onToggleChecklistItem,
  roomLastSeen,
  roomEntrySeenCount,
  onAddTodo,
  onToggleTodo,
  onRemoveTodo,
  onLogDecision,
  onRemoveDecision,
  onUpdateDecisionStatus,
  onAddShortList,
  onAddShortListItem,
  onRemoveShortListItem,
  onToggleShortListItemPin,
  onRemoveShortList,
  onToggleReaction,
  onEditMessage,
  onDeleteMessage,
  onTogglePin,
  onUpdateObjective,
  onUpdateIcon,
  onSummarize,
  socket,
  onlineUsers,
  notifications = [],
  unreadNotificationCount = 0,
  onMarkNotificationRead,
  onMarkAllNotificationsRead,
  workspaceId,
  onSwitchThread,
  aiPrefs,
  onToggleMuteAI,
  onSetPrimaryAI,
  // Desktop three-pane shell: default the in-thread right panel closed so the
  // center pane stays conversation-focused and the shell's Work rail carries
  // the workspace decisions/todos. The header toggle still opens it on demand.
  collapseRightPanel = false,
}) {
  const [input, setInput] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [aiTyping, setAiTyping] = useState([]);
  const [streamingMessages, setStreamingMessages] = useState({});
  const isDesktopViewport = () =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1025px)').matches;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(
    collapseRightPanel ? false : isDesktopViewport,
  );
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  // Thread stats (decision/artifact/todo/message counts + people/AI count)
  // are collapsed by default so the header row stays out of the chat's way;
  // the chevron in the header expands them on demand.
  const [statsExpanded, setStatsExpanded] = useState(false);
  // In-thread search: a magnifying glass near the bell expands an inline
  // search bar that can scope to this thread or the whole workspace.
  const [searchOpen, setSearchOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [repliesRootId, setRepliesRootId] = useState(null);
  const [editingObjective, setEditingObjective] = useState(false);
  const [objectiveDraft, setObjectiveDraft] = useState('');
  const [savingObjective, setSavingObjective] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [decisionSources, setDecisionSources] = useState({});
  const [siblingThreadSummary, setSiblingThreadSummary] = useState([]);
  // Send intent — ONE exclusive composer chip selection:
  //   'collaborate' (Symphonate, default — humans + AI both decide whether to
  //   respond), 'team' (no AI unless explicitly @mentioned), or 'ask:<aiId>'
  //   (@Aimy / @Aiden — inserts that AI's @mention so it answers directly).
  // A direct-ask selection is remembered in localStorage so subsequent
  // messages keep going to the same AI without re-selecting; Team is a
  // per-message choice and resets to Symphonate after sending.
  const [sendIntent, setSendIntent] = useState(() => {
    try {
      const stored = localStorage.getItem('symphonate-ai-chip');
      return stored ? `ask:${stored}` : 'collaborate';
    } catch {
      return 'collaborate';
    }
  });
  const [inputFocused, setInputFocused] = useState(false);
  const [slashQuery, setSlashQuery] = useState(null);
  const [slashIndex, setSlashIndex] = useState(0);
  const [toastMessage, setToastMessage] = useState(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [unreadDismissed, setUnreadDismissed] = useState(false);
  const [activeStatTooltip, setActiveStatTooltip] = useState(null);
  const prevDecisionCountRef = useRef((decisions || []).length);
  const toastTimeoutRef = useRef(null);
  const statTooltipTimeoutRef = useRef(null);
  const { uploadFile, uploading } = useFileUpload();
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const sendingRef = useRef(false);
  const justSentRef = useRef(false);

  // Grow the composer to fit its content so users can see more of what they
  // type, up to a max-height (matching the textarea's CSS cap) after which it
  // scrolls. We measure from a reset height so the box can also shrink back as
  // text is deleted.
  const COMPOSER_MAX_HEIGHT = 200;
  const resizeTextarea = useCallback(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, []);

  // Resize the composer after every input change, before the browser paints
  // (useLayoutEffect) so the box never visibly jumps.
  useLayoutEffect(() => {
    resizeTextarea();
  }, [input, resizeTextarea]);

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Clean up typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      if (statTooltipTimeoutRef.current) clearTimeout(statTooltipTimeoutRef.current);
    };
  }, []);

  // Track whether the user has scrolled away from the bottom so we can
  // surface the floating "jump to new" chevron. Reaching the bottom also
  // clears the unread divider — the user has now seen those messages.
  useEffect(() => {
    const el = messagesListRef.current;
    if (!el) return undefined;
    const distanceFromBottom = () => el.scrollHeight - el.scrollTop - el.clientHeight;
    const onScroll = () => {
      const distance = distanceFromBottom();
      setShowScrollDown(distance > 200);
      if (distance < 80) setUnreadDismissed(true);
    };
    // Initial measure drives the chevron only — never auto-dismiss the
    // unread divider on mount, since the user may not have looked yet.
    setShowScrollDown(distanceFromBottom() > 200);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [room.id, messages.length]);

  // The floating chevron always jumps to the very bottom so the newest
  // message is fully in view. scrollTo on the list container (rather than
  // scrollIntoView on the zero-height end marker) guarantees we land flush
  // against the bottom edge.
  const scrollToBottom = useCallback(() => {
    const el = messagesListRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Phone tooltip: tap a stat cell to surface its label for ~2s. On desktop
  // hovers, the native `title` attribute keeps doing the work.
  const handleStatTap = useCallback((statKey) => {
    setActiveStatTooltip(statKey);
    if (statTooltipTimeoutRef.current) clearTimeout(statTooltipTimeoutRef.current);
    statTooltipTimeoutRef.current = setTimeout(() => setActiveStatTooltip(null), 2200);
  }, []);

  // Surface a "Decision logged" toast whenever a new decision is added.
  // Compares prev vs current length so AI-logged decisions are visible even
  // when the user is on a different right-panel tab.
  useEffect(() => {
    const len = (decisions || []).length;
    if (len > prevDecisionCountRef.current) {
      setToastMessage('✓ Decision logged');
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = setTimeout(() => setToastMessage(null), 3000);
    }
    prevDecisionCountRef.current = len;
  }, [decisions]);

  // Smart auto-scroll: only scroll if user is near the bottom
  const isNearBottom = useCallback(() => {
    const el = messagesListRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 150;
  }, []);

  // Initial-landing scroll: on first room entry, jump to the first unread
  // message so the user picks up where they left off instead of at the top
  // (or having to manually scroll up from the bottom). Only fires once per
  // room — subsequent message updates fall through to the smart auto-scroll.
  const initialScrollDoneRef = useRef(false);
  useEffect(() => {
    initialScrollDoneRef.current = false;
    setUnreadDismissed(false);
  }, [room.id]);

  useEffect(() => {
    if (initialScrollDoneRef.current) return;
    if (!messages.length) return;

    const el = messagesListRef.current;
    const seen = roomEntrySeenCount;
    const chatMessages = messages.filter((m) => m.senderType !== 'system');

    const jumpToBottom = () => {
      if (el) {
        el.scrollTop = el.scrollHeight;
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }
    };

    if (el && typeof seen === 'number' && seen > 0 && seen < chatMessages.length) {
      // Prefer landing on the "Unread" divider so the user immediately sees
      // where they left off; fall back to the first unread message itself.
      const firstUnread = chatMessages[seen];
      const msgEl = firstUnread && document.getElementById(`msg-${firstUnread.id}`);
      const target = document.getElementById('unread-divider') || msgEl;
      if (target) {
        // If everything from the unread point down already fits on screen,
        // scroll all the way to the bottom so the newest message lands flush
        // against the composer and is shown in full — never cut off. Only when
        // the unread block is taller than the viewport do we pin its top to the
        // top of the screen so the user can read down from where they left off.
        const containerRect = el.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const targetOffset = targetRect.top - containerRect.top + el.scrollTop;
        const contentBelowTarget = el.scrollHeight - targetOffset;
        if (contentBelowTarget <= el.clientHeight) {
          jumpToBottom();
        } else {
          target.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
        if (msgEl) {
          msgEl.classList.add('highlight-flash');
          setTimeout(() => msgEl.classList.remove('highlight-flash'), 1600);
        }
        initialScrollDoneRef.current = true;
        return;
      }
    }

    jumpToBottom();
    initialScrollDoneRef.current = true;
  }, [messages, roomEntrySeenCount, room.id]);

  useEffect(() => {
    if (!initialScrollDoneRef.current) return;
    // Always follow the user's own just-sent message to the bottom, even if
    // they had scrolled up; otherwise fall back to the near-bottom heuristic.
    if (justSentRef.current || isNearBottom()) {
      justSentRef.current = false;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, streamingMessages, isNearBottom]);

  // Socket event handlers for typing and AI streaming
  useEffect(() => {
    const handleUserTyping = ({ userId, userName }) => {
      setTypingUsers((prev) => {
        if (prev.find((u) => u.userId === userId)) return prev;
        return [...prev, { userId, userName }];
      });
    };

    const handleUserStoppedTyping = ({ userId }) => {
      setTypingUsers((prev) => prev.filter((u) => u.userId !== userId));
    };

    const handleAiTyping = ({ aiId, aiName }) => {
      setAiTyping((prev) => {
        if (prev.find((a) => a.aiId === aiId)) return prev;
        return [...prev, { aiId, aiName }];
      });
    };

    const handleAiStoppedTyping = ({ aiId }) => {
      setAiTyping((prev) => prev.filter((a) => a.aiId !== aiId));
    };

    const handleAiStreamStart = ({ messageId, aiId, aiName }) => {
      setStreamingMessages((prev) => ({
        ...prev,
        [messageId]: { aiId, aiName, content: '' },
      }));
    };

    const handleAiStreamToken = ({ messageId, token }) => {
      setStreamingMessages((prev) => {
        const existing = prev[messageId];
        if (!existing) return prev;
        return {
          ...prev,
          [messageId]: { ...existing, content: existing.content + token },
        };
      });
    };

    const handleAiStreamEnd = ({ messageId }) => {
      setStreamingMessages((prev) => {
        const updated = { ...prev };
        delete updated[messageId];
        return updated;
      });
    };

    const handleDecisionSourcesUpdated = ({ sources }) => {
      if (sources) setDecisionSources(sources);
    };

    // If the socket drops mid-stream, the matching ai-stream-end is emitted
    // to the room while we're offline and we never receive it — so the
    // streaming bubble would linger after reconnect and surface as a "ghost"
    // earlier-prompt response next to the next AI turn. Drop any in-flight
    // stream buffers on disconnect; the finalised message will arrive via
    // 'message' / join-room on reconnect.
    const handleDisconnect = () => {
      setStreamingMessages({});
    };

    socket.on('user-typing', handleUserTyping);
    socket.on('user-stopped-typing', handleUserStoppedTyping);
    socket.on('ai-typing', handleAiTyping);
    socket.on('ai-stopped-typing', handleAiStoppedTyping);
    socket.on('ai-stream-start', handleAiStreamStart);
    socket.on('ai-stream-token', handleAiStreamToken);
    socket.on('ai-stream-end', handleAiStreamEnd);
    socket.on('decision-sources-updated', handleDecisionSourcesUpdated);
    socket.on('disconnect', handleDisconnect);

    // Initial fetch on room entry so existing provenance shows up without
    // waiting for a refresh event.
    socket.emit('get-decision-sources', { roomId: room.id }, (response) => {
      if (response?.success && response.sources) {
        setDecisionSources(response.sources);
      }
    });

    // Fetch a workspace context summary for the AI Context panel.
    socket.emit('get-workspace-context-summary', { roomId: room.id }, (response) => {
      if (response?.success && Array.isArray(response.siblingThreads)) {
        setSiblingThreadSummary(response.siblingThreads);
      }
    });

    return () => {
      socket.off('user-typing', handleUserTyping);
      socket.off('user-stopped-typing', handleUserStoppedTyping);
      socket.off('ai-typing', handleAiTyping);
      socket.off('ai-stopped-typing', handleAiStoppedTyping);
      socket.off('ai-stream-start', handleAiStreamStart);
      socket.off('ai-stream-token', handleAiStreamToken);
      socket.off('ai-stream-end', handleAiStreamEnd);
      socket.off('decision-sources-updated', handleDecisionSourcesUpdated);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket, room.id]);

  // Debounced typing: only emit typing-start once per burst
  const emitTyping = useCallback(() => {
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socket.emit('typing-start', { roomId: room.id });
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
      socket.emit('typing-stop', { roomId: room.id });
    }, 2000);
  }, [socket, room.id]);

  // The remembered direct-ask AI, resolved against the AIs actually in this
  // room (a stale stored id — e.g. Aiden after Gemini was unconfigured —
  // simply deselects).
  const askAiId = sendIntent.startsWith('ask:') ? sendIntent.slice(4) : null;
  const selectedAskAI = askAiId
    ? (room.aiParticipants || []).find((a) => a.id === askAiId) || null
    : null;

  // Drop any AI's @mention from the very start of the draft — used when the
  // selection moves away from a direct-ask chip.
  const stripLeadingAIMention = (text) => {
    let next = text;
    for (const ai of room.aiParticipants || []) {
      next = next.replace(new RegExp(`^@${ai.name}\\s*`, 'i'), '');
    }
    return next;
  };

  const clearStoredAIChip = () => {
    try {
      localStorage.removeItem('symphonate-ai-chip');
    } catch {
      /* storage unavailable */
    }
  };

  // Symphonate / Team chips. The four chips are mutually exclusive: switching
  // here away from a direct-ask (@Aimy / @Aiden) forgets that selection and
  // removes the inserted @mention from the draft.
  const selectIntent = (intent) => {
    if (sendIntent.startsWith('ask:')) {
      clearStoredAIChip();
      setInput((prev) => stripLeadingAIMention(prev));
    }
    setSendIntent(intent);
  };

  // @Aimy / @Aiden chip: selecting inserts the mention at the start of the
  // draft (swapping out the other AI's leading mention if present); clicking
  // the active chip again deselects back to Symphonate and strips it.
  // Selection persists across messages and sessions via localStorage.
  const toggleAIChip = (ai) => {
    if (sendIntent === `ask:${ai.id}`) {
      clearStoredAIChip();
      setSendIntent('collaborate');
      setInput((prev) => prev.replace(new RegExp(`^@${ai.name}\\s*`, 'i'), ''));
    } else {
      try {
        localStorage.setItem('symphonate-ai-chip', ai.id);
      } catch {
        /* storage unavailable */
      }
      setSendIntent(`ask:${ai.id}`);
      setInput((prev) => {
        let next = prev;
        for (const other of room.aiParticipants || []) {
          if (other.id !== ai.id) {
            next = next.replace(new RegExp(`^@${other.name}\\s*`, 'i'), '');
          }
        }
        if (aiMentionRegex(ai.name).test(next)) return next;
        return `@${ai.name} ${next}`;
      });
    }
    inputRef.current?.focus();
  };

  const handleSend = (e) => {
    e.preventDefault();
    const hasText = input.trim().length > 0;
    const hasFiles = pendingAttachments.length > 0;
    if ((!hasText && !hasFiles) || sendingRef.current) return;
    sendingRef.current = true;

    if (editingMessage) {
      onEditMessage(editingMessage.id, input.trim());
      setEditingMessage(null);
    } else {
      // The user is sending their own message — flag it so the next render
      // scrolls to the bottom regardless of current scroll position.
      justSentRef.current = true;
      const attachmentIds = pendingAttachments.map((a) => a.id);
      let content = input.trim();
      // Remembered direct-ask: keep routing to the selected AI on every
      // message without re-typing the mention. Skipped for slash/private
      // messages and when the user already mentioned an AI themselves.
      if (
        selectedAskAI &&
        content &&
        !content.startsWith('/') &&
        !(room.aiParticipants || []).some((a) => aiMentionRegex(a.name).test(content))
      ) {
        content = `@${selectedAskAI.name} ${content}`;
      }
      onSendMessage(
        content || '',
        replyTo?.id || null,
        attachmentIds.length > 0 ? attachmentIds : undefined,
        {
          skipAITrigger: sendIntent === 'team',
          // Direct asks carry the 'ai' intent badge; a stale ask selection
          // (AI no longer in the room) degrades to the Symphonate default.
          intent: selectedAskAI ? 'ai' : sendIntent.startsWith('ask:') ? 'collaborate' : sendIntent,
        },
      );
    }

    // A remembered direct-ask stays sticky: re-prime the box with the AI's
    // visible @mention so the user can see the next message is still routed to
    // that AI alone — and the routing rides on the real text, not just on
    // send-time state that could fall back to the primary AI. Edits and the
    // other chips just clear the box.
    setInput(selectedAskAI && !editingMessage ? `@${selectedAskAI.name} ` : '');
    setReplyTo(null);
    setPendingAttachments([]);
    // Team is per-message and falls back to Symphonate; a remembered
    // direct-ask (and Symphonate itself) stays selected for the next message.
    if (sendIntent === 'team') setSendIntent('collaborate');
    isTypingRef.current = false;
    socket.emit('typing-stop', { roomId: room.id });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    inputRef.current?.focus();
    setTimeout(() => {
      sendingRef.current = false;
    }, 300);
  };

  const handleFilesSelected = useCallback(
    async (files) => {
      for (const file of files) {
        try {
          const attachment = await uploadFile(file, room.id);
          setPendingAttachments((prev) => [...prev, attachment]);
        } catch {
          /* upload error handled by hook */
        }
      }
    },
    [uploadFile, room.id],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) handleFilesSelected(files);
    },
    [handleFilesSelected],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const downloadBlob = useCallback((blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const safeFilename = useCallback((base, ext) => {
    const stem = (base || 'thread').replace(/[^a-zA-Z0-9_-]+/g, '-').slice(0, 40) || 'thread';
    return `${stem}.${ext}`;
  }, []);

  const handleExportMarkdown = useCallback(() => {
    const token = localStorage.getItem('symphonate-token');
    fetch(`/api/rooms/${room.id}/export.md`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => {
        if (!r.ok) throw new Error('Export failed');
        return r.blob();
      })
      .then((blob) => downloadBlob(blob, safeFilename(room.name, 'md')))
      .catch((err) => console.error(err));
  }, [room.id, room.name, downloadBlob, safeFilename]);

  const handleExportJson = useCallback(() => {
    const payload = {
      thread: {
        id: room.id,
        name: room.name,
        template: room.template || null,
        objective: room.objective || null,
      },
      exported_at: new Date().toISOString(),
      messages: messages.map((m) => ({
        id: m.id,
        senderId: m.senderId,
        senderName: m.senderName,
        senderType: m.senderType,
        content: m.content,
        replyTo: m.replyTo || null,
        reactions: m.reactions || {},
        pinned: !!m.pinned,
        edited: !!m.edited,
        timestamp: m.timestamp,
      })),
      artifacts: (artifacts || []).map((a) => ({
        id: a.id,
        title: a.title,
        type: a.type,
        content: a.content,
        updatedAt: a.updatedAt,
        updatedBy: a.updatedBy,
      })),
      decisions: decisions || [],
      todos: todos || [],
      shortLists: shortLists || [],
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    downloadBlob(blob, safeFilename(room.name, 'json'));
  }, [room, messages, artifacts, decisions, todos, shortLists, downloadBlob, safeFilename]);

  const handleReply = useCallback((msg) => {
    setReplyTo(msg);
    setEditingMessage(null);
    inputRef.current?.focus();
  }, []);

  const handleEdit = useCallback((msg) => {
    setEditingMessage(msg);
    setInput(msg.content);
    setReplyTo(null);
    inputRef.current?.focus();
  }, []);

  const handleDelete = useCallback(
    (msg) => {
      if (window.confirm('Delete this message?')) {
        onDeleteMessage(msg.id);
      }
    },
    [onDeleteMessage],
  );

  const handlePin = useCallback(
    (msg) => {
      onTogglePin(msg.id);
    },
    [onTogglePin],
  );

  const flashToast = useCallback((text) => {
    setToastMessage(text);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToastMessage(null), 2200);
  }, []);

  // Direction B one-tap capture: promote a message straight into a decision or
  // a todo. Decisions surface their own "logged" toast via the decisions-diff
  // effect above; todos get an explicit confirmation here.
  const handleCaptureDecision = useCallback(
    (msg) => {
      const text = (msg.content || '').trim();
      if (!onLogDecision || !text) return;
      const title = text.length > 60 ? `${text.slice(0, 57).trimEnd()}…` : text;
      onLogDecision(title, text);
    },
    [onLogDecision],
  );

  const handleCaptureTodo = useCallback(
    (msg) => {
      const text = (msg.content || '').trim();
      if (!onAddTodo || !text) return;
      onAddTodo(text);
      flashToast('✓ Added to your todos');
    },
    [onAddTodo, flashToast],
  );

  const startEditingObjective = useCallback(() => {
    setObjectiveDraft(room.objective || '');
    setEditingObjective(true);
  }, [room.objective]);

  const cancelEditingObjective = useCallback(() => {
    setEditingObjective(false);
    setObjectiveDraft('');
  }, []);

  const saveObjective = useCallback(async () => {
    if (!onUpdateObjective) return;
    setSavingObjective(true);
    const result = await onUpdateObjective(objectiveDraft);
    setSavingObjective(false);
    if (!result?.error) {
      setEditingObjective(false);
      setObjectiveDraft('');
    }
  }, [onUpdateObjective, objectiveDraft]);

  // Compact this thread's AI context: fold the older conversation into the
  // AI's memory ledger so future replies stay fast and focused. Non-destructive
  // — chat history, decisions, and artifacts are untouched. The server posts
  // an ephemeral confirmation message, so we don't need to render one here.
  const [compacting, setCompacting] = useState(false);
  const handleCompact = useCallback(() => {
    if (!socket || !room?.id || compacting) return;
    setCompacting(true);
    socket.emit('compact-thread', { roomId: room.id }, () => {
      setCompacting(false);
    });
  }, [socket, room, compacting]);

  // Combine all participants (human + AI) for display and @mention coloring.
  // Live room participants and AIs come first; then every signed-up workspace
  // member who isn't already present is folded in (deduped by user id) so the
  // whole team is taggable — not just whoever currently has the thread open.
  // Absent members get a deterministic fallback colour so their mention still
  // renders coloured and recognizable.
  const allParticipants = useMemo(() => {
    const base = [...participants, ...(room.aiParticipants || [])];
    const seen = new Set(base.map((p) => p.id).filter(Boolean));
    const seenNames = new Set(base.map((p) => p.name?.toLowerCase()).filter(Boolean));
    for (const m of workspaceMembers || []) {
      const id = m.userId || m.id;
      const nameKey = m.name?.toLowerCase();
      if (!m.name || (id && seen.has(id)) || (nameKey && seenNames.has(nameKey))) continue;
      if (id) seen.add(id);
      if (nameKey) seenNames.add(nameKey);
      base.push({
        id,
        name: m.name,
        type: 'human',
        avatar: m.avatar || null,
        color: colorForId(id || m.name),
      });
    }
    return base;
  }, [participants, room.aiParticipants, workspaceMembers]);

  // Thread phase inferred from decisions, todos and message volume.
  // Used to give users orientation on where the thread is in its lifecycle.
  const activeDecisionCount = (decisions || []).filter((d) => d.status === 'active').length;
  const openTodoCount = (todos || []).filter((t) => !t.completed).length;
  const inferThreadPhase = () => {
    if (activeDecisionCount === 0 && messages.length < 10) return 'Ideating';
    if (activeDecisionCount > 0 && openTodoCount > 0) return 'Converging';
    if ((decisions || []).length > 0 && openTodoCount === 0) return 'Executed';
    return 'Converging';
  };
  const threadPhase = inferThreadPhase();

  // Date separators helper
  const getDateLabel = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Get filtered mention suggestions
  const mentionSuggestions =
    mentionQuery !== null
      ? allParticipants.filter((p) => p.name.toLowerCase().includes(mentionQuery.toLowerCase()))
      : [];

  const insertMention = useCallback(
    (name) => {
      const textarea = inputRef.current;
      if (!textarea) return;
      const cursorPos = textarea.selectionStart;
      const textBefore = input.slice(0, cursorPos);
      const textAfter = input.slice(cursorPos);
      const atIndex = textBefore.lastIndexOf('@');
      if (atIndex === -1) return;
      const newText = textBefore.slice(0, atIndex) + '@' + name + ' ' + textAfter;
      setInput(newText);
      setMentionQuery(null);
      setMentionIndex(0);
      // Focus and set cursor position after React re-renders
      setTimeout(() => {
        textarea.focus();
        const newPos = atIndex + name.length + 2;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    },
    [input],
  );

  const handleKeyDown = (e) => {
    // Handle slash command palette navigation first when it's open
    if (slashQuery !== null) {
      const filtered = filterSlashCommands(slashQuery);
      if (filtered.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSlashIndex((prev) => (prev + 1) % filtered.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSlashIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          insertSlashCommand(filtered[slashIndex] || filtered[0]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashQuery(null);
        return;
      }
    }
    // Handle mention autocomplete navigation
    if (mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % mentionSuggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(
          (prev) => (prev - 1 + mentionSuggestions.length) % mentionSuggestions.length,
        );
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionSuggestions[mentionIndex].name);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }
    // Escape cancels reply/edit
    if (e.key === 'Escape') {
      if (editingMessage) {
        setEditingMessage(null);
        setInput('');
        return;
      }
      if (replyTo) {
        setReplyTo(null);
        return;
      }
    }
    // Enter inserts a newline; send with the button or Ctrl/Cmd+Enter.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    emitTyping();

    // Detect @mention in progress
    const cursorPos = e.target.selectionStart;
    const textBefore = value.slice(0, cursorPos);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }

    // Detect a leading slash command — only when the / is the very first char
    // and the user hasn't yet typed a space (i.e. still picking the command).
    if (value.startsWith('/') && !value.includes(' ')) {
      setSlashQuery(value.slice(1));
      setSlashIndex(0);
    } else {
      setSlashQuery(null);
    }
  };

  const insertSlashCommand = useCallback((cmd) => {
    setInput(cmd.template);
    setSlashQuery(null);
    setSlashIndex(0);
    setTimeout(() => {
      const ta = inputRef.current;
      if (ta) {
        ta.focus();
        const pos = cmd.template.length;
        ta.setSelectionRange(pos, pos);
      }
    }, 0);
  }, []);

  // First unread message — the chat message at the seen-count boundary
  // captured on room entry. Drives the "Unread" divider below.
  let firstUnreadId = null;
  if (typeof roomEntrySeenCount === 'number' && roomEntrySeenCount > 0) {
    const chatMessages = messages.filter((m) => m.senderType !== 'system');
    if (roomEntrySeenCount < chatMessages.length) {
      firstUnreadId = chatMessages[roomEntrySeenCount].id;
    }
  }

  // Build messages list with date + unread separators
  let lastDateLabel = null;
  const filteredMessages = messages;
  const messagesWithSeparators = [];
  for (const msg of filteredMessages) {
    const label = getDateLabel(msg.timestamp);
    if (label !== lastDateLabel) {
      messagesWithSeparators.push({ type: 'date-separator', label, key: `sep-${msg.id}` });
      lastDateLabel = label;
    }
    if (msg.id === firstUnreadId && !unreadDismissed) {
      messagesWithSeparators.push({ type: 'unread-separator', key: `unread-${msg.id}` });
    }
    messagesWithSeparators.push({ type: 'message', msg, key: msg.id });
  }

  // Count direct replies for each message (for thread badge)
  const replyCountsByParent = {};
  for (const m of messages) {
    if (m.replyTo) {
      replyCountsByParent[m.replyTo] = (replyCountsByParent[m.replyTo] || 0) + 1;
    }
  }

  const handleOpenReplies = useCallback((msg) => {
    setRepliesRootId(msg.id);
  }, []);

  const jumpToMessage = useCallback((mid) => {
    if (!mid) return;
    const el = document.getElementById(`msg-${mid}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('highlight-flash');
    setTimeout(() => el.classList.remove('highlight-flash'), 1600);
  }, []);

  // Swipe-right-to-dismiss on mobile for sidebar / right panel / thread panel.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const isMobile = () => window.innerWidth <= 640;
    if (!isMobile()) return undefined;

    const attachSwipe = (selector, onClose) => {
      const el = document.querySelector(selector);
      if (!el) return () => {};
      let startX = 0;
      let startY = 0;
      let tracking = false;
      let directionLocked = false;

      const reset = () => {
        el.classList.remove('swiping');
        el.style.transform = '';
      };

      const onStart = (e) => {
        if (!isMobile()) return;
        const t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        tracking = false;
        directionLocked = false;
      };

      const onMove = (e) => {
        const t = e.touches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (!directionLocked && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          directionLocked = true;
          if (Math.abs(dx) > Math.abs(dy) && dx > 0) {
            tracking = true;
            el.classList.add('swiping');
          }
        }
        if (tracking) {
          const offset = Math.max(0, dx);
          el.style.transform = `translateX(${offset}px)`;
        }
      };

      const onEnd = (e) => {
        if (!tracking) {
          reset();
          return;
        }
        const endX = (e.changedTouches && e.changedTouches[0]?.clientX) ?? startX;
        const dx = endX - startX;
        reset();
        if (dx > Math.min(80, el.offsetWidth * 0.25)) {
          onClose();
        }
        tracking = false;
      };

      el.addEventListener('touchstart', onStart, { passive: true });
      el.addEventListener('touchmove', onMove, { passive: true });
      el.addEventListener('touchend', onEnd);
      el.addEventListener('touchcancel', onEnd);

      return () => {
        el.removeEventListener('touchstart', onStart);
        el.removeEventListener('touchmove', onMove);
        el.removeEventListener('touchend', onEnd);
        el.removeEventListener('touchcancel', onEnd);
        reset();
      };
    };

    const cleanups = [];
    if (sidebarOpen) cleanups.push(attachSwipe('.sidebar', () => setSidebarOpen(false)));
    if (rightPanelOpen) cleanups.push(attachSwipe('.right-panel', () => setRightPanelOpen(false)));
    if (repliesRootId) cleanups.push(attachSwipe('.replies-panel', () => setRepliesRootId(null)));
    return () => cleanups.forEach((fn) => fn && fn());
  }, [sidebarOpen, rightPanelOpen, repliesRootId]);

  // Stat metadata for the consolidated header row. Tapping any cell on a
  // touch device surfaces the full label as a transient tooltip.
  const statCells = [
    {
      key: 'decisions',
      icon: <IconDecision size={14} />,
      num: activeDecisionCount,
      label: activeDecisionCount === 1 ? 'Decision' : 'Decisions',
      title: `${activeDecisionCount} active ${activeDecisionCount === 1 ? 'decision' : 'decisions'} in this thread`,
    },
    {
      key: 'artifacts',
      icon: <IconArtifact size={14} />,
      num: (artifacts || []).length,
      label: (artifacts || []).length === 1 ? 'Artifact' : 'Artifacts',
      title: `${(artifacts || []).length} ${(artifacts || []).length === 1 ? 'artifact' : 'artifacts'} (docs, diagrams, notes)`,
    },
    {
      key: 'todos',
      icon: <IconTodo size={14} />,
      num: openTodoCount,
      label: openTodoCount === 1 ? 'Todo' : 'Todos',
      title: `${openTodoCount} open ${openTodoCount === 1 ? 'todo' : 'todos'}`,
    },
    {
      key: 'messages',
      icon: <IconMessage size={14} />,
      num: messages.length,
      label: 'Msgs',
      title: `${messages.length} ${messages.length === 1 ? 'message' : 'messages'} so far`,
    },
  ];

  return (
    <div className="chat-room">
      {/* Compact header:
            Line 1: back · title · template · header-icons (chevron, people, board, more)
            Line 2: thread stats + people/AI count — collapsed by default,
                    toggled open by the chevron so the row frees up for chat
            Line 3: search bar
          Objective, when set, appears as a separate slim row beneath. */}
      <header className="chat-header">
        <div className="chat-header-row chat-header-row-title">
          <div className="chat-header-left">
            <BackButton onClick={onLeaveThread} title="Back to threads" />
            <EditableIcon
              icon={room.icon}
              name={room.name}
              size={22}
              canEdit={!!onUpdateIcon}
              onChange={(value) => onUpdateIcon && onUpdateIcon(value)}
              title="Change thread icon"
            />
            <div className="chat-title-block">
              <h2 title={room.name}>{room.name}</h2>
              <span className="chat-subtitle">
                {participants.length} {participants.length === 1 ? 'person' : 'people'}
                {(room.aiParticipants || []).length > 0 &&
                  ` · ${(room.aiParticipants || []).length} AI`}
              </span>
            </div>
          </div>
          <div className="chat-header-right">
            {onMarkNotificationRead && (
              <NotificationBell
                notifications={notifications}
                unreadCount={unreadNotificationCount}
                onMarkRead={onMarkNotificationRead}
                onMarkAllRead={onMarkAllNotificationsRead}
                onNavigate={(roomId) => {
                  if (roomId !== room.id) {
                    onLeaveThread();
                  }
                }}
              />
            )}
            <button
              className="btn-icon-only"
              onClick={() => {
                setSidebarOpen(!sidebarOpen);
                if (!sidebarOpen && window.innerWidth <= 1024) {
                  setRightPanelOpen(false);
                  setRepliesRootId(null);
                }
              }}
              title="Participants"
              aria-label="Toggle participants panel"
              aria-expanded={sidebarOpen}
            >
              <IconPeople size={16} />
            </button>
            <button
              className="btn-icon-only"
              onClick={() => {
                setRightPanelOpen(!rightPanelOpen);
                if (!rightPanelOpen && window.innerWidth <= 1024) {
                  setSidebarOpen(false);
                  setRepliesRootId(null);
                }
              }}
              title="Artifacts, todos & decisions"
              aria-label="Toggle artifacts and todos panel"
              aria-expanded={rightPanelOpen}
            >
              <IconBoard size={16} />
            </button>
            <div className="overflow-menu-wrapper">
              <button
                className="btn-icon-only btn-more"
                onClick={() => setMoreMenuOpen((v) => !v)}
                title="More actions"
                aria-label="More actions"
                aria-expanded={moreMenuOpen}
                aria-haspopup="menu"
              >
                <IconMore size={16} />
              </button>
              {moreMenuOpen && (
                <>
                  <div className="overflow-menu-backdrop" onClick={() => setMoreMenuOpen(false)} />
                  <div className="overflow-menu" role="menu">
                    {workspaceId && (
                      <button
                        className="overflow-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setMoreMenuOpen(false);
                          setSearchOpen((v) => !v);
                        }}
                      >
                        <IconSearch size={14} />
                        <span>Search in thread</span>
                      </button>
                    )}
                    {(roomLastSeen || messages.length >= 3) && (
                      <button
                        className="overflow-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setMoreMenuOpen(false);
                          onSummarize(roomLastSeen);
                        }}
                      >
                        <IconCatchUp size={14} />
                        <span>Catch up</span>
                      </button>
                    )}
                    <button
                      className="overflow-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setMoreMenuOpen(false);
                        setStatsExpanded((v) => !v);
                      }}
                    >
                      <IconChevronDown size={14} />
                      <span>{statsExpanded ? 'Hide thread stats' : 'Show thread stats'}</span>
                    </button>
                    <div className="overflow-menu-divider" role="separator" />
                    {onUpdateObjective && (
                      <button
                        className="overflow-menu-item"
                        role="menuitem"
                        onClick={() => {
                          setMoreMenuOpen(false);
                          startEditingObjective();
                        }}
                      >
                        <IconObjective size={14} />
                        <span>{room.objective ? 'Edit objective' : 'Add objective'}</span>
                      </button>
                    )}
                    <button
                      className="overflow-menu-item"
                      role="menuitem"
                      disabled={compacting}
                      onClick={() => {
                        setMoreMenuOpen(false);
                        handleCompact();
                      }}
                      title="Fold older messages into the AI's memory to keep replies fast (chat history is kept)"
                    >
                      <IconCompact size={14} />
                      <span>{compacting ? 'Compacting…' : 'Compact context'}</span>
                    </button>
                    <div className="overflow-menu-divider" role="separator" />
                    <div className="overflow-menu-label">Export</div>
                    <button
                      className="overflow-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setMoreMenuOpen(false);
                        handleExportMarkdown();
                      }}
                    >
                      <IconMarkdown size={14} />
                      <span>Markdown (.md)</span>
                    </button>
                    <button
                      className="overflow-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setMoreMenuOpen(false);
                        handleExportJson();
                      }}
                    >
                      <IconJson size={14} />
                      <span>JSON (.json)</span>
                    </button>
                    {onLogout && (
                      <>
                        <div className="overflow-menu-divider" role="separator" />
                        <button
                          className="overflow-menu-item"
                          role="menuitem"
                          onClick={() => {
                            setMoreMenuOpen(false);
                            onLogout();
                          }}
                        >
                          <IconLogout size={14} />
                          <span>Log out</span>
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
            <span
              className={`status-dot ${connected ? 'online' : 'offline'}`}
              title={connected ? 'Connected' : 'Disconnected'}
              aria-label={connected ? 'Connected' : 'Disconnected'}
            />
          </div>
        </div>

        {/* Row 2: thread status (phase) + template tag, kept off the title row
            so the title can never crowd the header icons. */}
        <div className="chat-header-row chat-header-row-meta">
          <span
            className={`thread-phase-badge phase-${threadPhase.toLowerCase()}`}
            title={`Phase: ${threadPhase}`}
          >
            {threadPhase}
          </span>
          {room.template && room.template !== 'freeform' && (
            <span className="thread-template-tag">{room.template}</span>
          )}
        </div>

        {/* Row 3: thread stats — collapsed by default to free the row for
            chat; toggled open by the chevron in the header. */}
        {statsExpanded && (
          <div
            className="chat-header-row chat-header-row-stats"
            role="group"
            aria-label="Thread stats"
          >
            <div className="thread-stats-cells">
              <span className="header-meta header-meta-people" title="People in this thread">
                <IconPeople size={12} />
                {participants.length}
                {(room.aiParticipants || []).length > 0 && (
                  <span className="header-meta-ai">+{(room.aiParticipants || []).length} AI</span>
                )}
              </span>
              {statCells.map((cell) => (
                <button
                  key={cell.key}
                  type="button"
                  className={`thread-stat-cell ${activeStatTooltip === cell.key ? 'tooltip-open' : ''}`}
                  title={cell.title}
                  aria-label={cell.title}
                  onClick={() => handleStatTap(cell.key)}
                >
                  <span className="thread-stat-icon" aria-hidden="true">
                    {cell.icon}
                  </span>
                  <span className="thread-stat-num">{cell.num}</span>
                  <span className="thread-stat-label">{cell.label}</span>
                  <span className="thread-stat-tooltip" role="tooltip">
                    {cell.title}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Expandable search bar — toggled by the magnifying glass above.
            Defaults to searching just this thread; the scope toggle inside
            the bar flips between thread-only and the whole workspace. */}
        {searchOpen && workspaceId && (
          <div className="chat-header-row chat-header-row-search">
            <SearchPanel
              workspaceId={workspaceId}
              roomId={room.id}
              roomName={room.name}
              onNavigate={(roomId) => {
                if (roomId !== room.id) {
                  onLeaveThread();
                  if (onSwitchThread) {
                    setTimeout(() => onSwitchThread(roomId), 80);
                  }
                }
                setSearchOpen(false);
              }}
              onClose={() => setSearchOpen(false)}
              inline
              autoFocus
            />
          </div>
        )}
      </header>

      {/* Thread objective — optional user-stated goal that the AI sees.
          The "add/edit" trigger lives in the header more-menu; this row only
          renders while editing, or as a read-only summary when one is set. */}
      {onUpdateObjective && (editingObjective || room.objective) && (
        <div className="thread-objective-row">
          {editingObjective ? (
            <div className="thread-objective-edit">
              <span className="thread-objective-icon" aria-hidden="true">
                <IconObjective size={16} />
              </span>
              <textarea
                className="thread-objective-input"
                value={objectiveDraft}
                onChange={(e) => setObjectiveDraft(e.target.value)}
                placeholder="What do you want to achieve in this thread?"
                rows={2}
                maxLength={1000}
                autoFocus
                disabled={savingObjective}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    saveObjective();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelEditingObjective();
                  }
                }}
              />
              <div className="thread-objective-actions">
                <button
                  type="button"
                  className="thread-objective-save"
                  onClick={saveObjective}
                  disabled={savingObjective}
                >
                  {savingObjective ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="thread-objective-cancel"
                  onClick={cancelEditingObjective}
                  disabled={savingObjective}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="thread-objective-view"
              onClick={startEditingObjective}
              title="Edit objective"
            >
              <span className="thread-objective-icon" aria-hidden="true">
                <IconObjective size={14} />
              </span>
              <span className="thread-objective-label">Objective:</span>
              <span className="thread-objective-text">{room.objective}</span>
            </button>
          )}
        </div>
      )}

      {/* Disconnect banner */}
      {!connected && <div className="disconnect-banner">Reconnecting...</div>}

      <div className="chat-body">
        {/* Messages */}
        <div className="messages-area">
          {/* Scrollable region: holds the messages list AND the floating
              "jump to latest" chevron, so the chevron anchors to the
              bottom of the conversation rather than the bottom of the
              composer/send area below. */}
          <div className="messages-scroll-region">
            <div className="messages-list" ref={messagesListRef}>
              {messagesWithSeparators.map((item) => {
                if (item.type === 'date-separator') {
                  return (
                    <div key={item.key} className="date-separator">
                      <span>{item.label}</span>
                    </div>
                  );
                }
                if (item.type === 'unread-separator') {
                  return (
                    <div
                      key={item.key}
                      id="unread-divider"
                      className="unread-separator"
                      role="separator"
                      aria-label="Unread messages below"
                    >
                      <span>Unread</span>
                    </div>
                  );
                }
                const msg = item.msg;
                // Find the replied-to message
                const repliedMsg = msg.replyTo ? messages.find((m) => m.id === msg.replyTo) : null;
                return (
                  <MessageBubble
                    key={msg.id}
                    message={msg}
                    isOwn={msg.senderId === user.id}
                    participants={allParticipants}
                    currentUserId={user.id}
                    onToggleReaction={onToggleReaction}
                    onReply={handleReply}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onPin={handlePin}
                    onAskAI={onInvokeAI ? (m) => onInvokeAI(m.id) : null}
                    onCaptureDecision={onLogDecision ? handleCaptureDecision : null}
                    onCaptureTodo={onAddTodo ? handleCaptureTodo : null}
                    repliedMessage={repliedMsg}
                    onOpenReplies={handleOpenReplies}
                    onJumpToMessage={jumpToMessage}
                    replyCount={replyCountsByParent[msg.id] || 0}
                    onCompact={handleCompact}
                  />
                );
              })}

              {/* Streaming AI messages. Hide any entry whose id has already
                  been persisted into `messages` — at that point the saved
                  bubble IS the response and the streaming buffer is stale
                  (e.g. ai-stream-end was missed because the socket blipped). */}
              {Object.entries(streamingMessages)
                .filter(([msgId]) => !messages.some((m) => m.id === msgId))
                .map(([msgId, stream]) => (
                  <MessageBubble
                    key={`stream-${msgId}`}
                    message={{
                      id: msgId,
                      senderId: stream.aiId,
                      senderName: stream.aiName,
                      senderType: 'ai',
                      content: stream.content,
                      timestamp: new Date().toISOString(),
                    }}
                    isOwn={false}
                    participants={allParticipants}
                    isStreaming
                  />
                ))}

              <div ref={messagesEndRef} />
            </div>

            {/* Scroll-to-latest chevron — only visible when the user has
              scrolled up. Anchored to the bottom of the messages-scroll-region
              so it sits above the typing indicator + input area, never
              overlapping the send button. */}
            {showScrollDown && (
              <button
                type="button"
                className="scroll-to-bottom-btn"
                onClick={scrollToBottom}
                aria-label="Jump to latest message"
                title="Jump to latest message"
              >
                <IconChevronDown size={20} />
              </button>
            )}
          </div>
          {/* /.messages-scroll-region */}

          {/* Typing indicators */}
          {(typingUsers.length > 0 || aiTyping.length > 0) && (
            <div className="typing-indicator">
              {typingUsers.map((u) => u.userName).join(', ')}
              {typingUsers.length > 0 && aiTyping.length > 0 && ', '}
              {aiTyping.map((a) => a.aiName).join(', ')}{' '}
              {typingUsers.length + aiTyping.length === 1 ? 'is' : 'are'} typing
              <span className="typing-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </div>
          )}

          {/* Error notice */}
          {error && <div className="error-notice">{error}</div>}

          {/* Transient toast (e.g. "Decision logged") — shared Direction B pill */}
          <Toast message={toastMessage} />

          {/* Input area */}
          <div className="input-wrapper">
            {/* Reply preview */}
            {replyTo && (
              <div className="reply-preview">
                <div className="reply-preview-content">
                  <span className="reply-preview-label">Replying to {replyTo.senderName}</span>
                  <span className="reply-preview-text">
                    {replyTo.content.length > 80
                      ? replyTo.content.slice(0, 80) + '...'
                      : replyTo.content}
                  </span>
                </div>
                <button className="reply-preview-close" onClick={() => setReplyTo(null)}>
                  x
                </button>
              </div>
            )}

            {/* Edit preview */}
            {editingMessage && (
              <div className="reply-preview editing">
                <div className="reply-preview-content">
                  <span className="reply-preview-label">Editing message</span>
                </div>
                <button
                  className="reply-preview-close"
                  onClick={() => {
                    setEditingMessage(null);
                    setInput('');
                  }}
                >
                  x
                </button>
              </div>
            )}

            {/* Private message hint */}
            {input.startsWith('//') && (
              <div className="private-hint">
                <span className="private-hint-icon">&#128274;</span>
                Hidden from the AI — your teammates still see this; it’s kept out of AI context only
              </div>
            )}

            {/* Intent chips — visible when composing */}
            {(inputFocused || input.length > 0) && !editingMessage && !input.startsWith('//') && (
              <div className="intent-chips" role="group" aria-label="Send intent">
                <button
                  type="button"
                  className={`intent-chip ${sendIntent === 'collaborate' ? 'active collaborate' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectIntent('collaborate')}
                  title="Symphonate — the team and the AI can both respond"
                >
                  <BrandMark size={15} mono={sendIntent === 'collaborate'} /> Symphonate
                </button>
                <button
                  type="button"
                  className={`intent-chip ${sendIntent === 'team' ? 'active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectIntent('team')}
                  title="Send to the team — AI stays quiet unless explicitly mentioned"
                >
                  <IconPeople size={13} /> Team
                </button>
                {(room.aiParticipants || []).map((ai) => {
                  const active = sendIntent === `ask:${ai.id}`;
                  return (
                    <button
                      key={ai.id}
                      type="button"
                      className={`intent-chip intent-chip-ai ${active ? 'active' : ''}`}
                      style={
                        active
                          ? { background: ai.color, borderColor: ai.color, color: '#f7f6f0' }
                          : { color: ai.color }
                      }
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggleAIChip(ai)}
                      title={
                        active
                          ? `Messages go to ${ai.name} — click to stop asking ${ai.name} directly`
                          : `Ask ${ai.name} directly — adds @${ai.name} to your message and stays selected for your next messages`
                      }
                    >
                      <IconSparkle size={13} /> @{ai.name}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Slash command palette */}
            {slashQuery !== null && (
              <SlashCommandMenu
                query={slashQuery}
                activeIndex={slashIndex}
                onSelect={insertSlashCommand}
                onDismiss={() => setSlashQuery(null)}
              />
            )}

            {/* @mention autocomplete dropdown */}
            {mentionSuggestions.length > 0 && (
              <div className="mention-dropdown">
                {mentionSuggestions.map((p, i) => (
                  <button
                    key={p.id}
                    className={`mention-item ${i === mentionIndex ? 'active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insertMention(p.name);
                    }}
                  >
                    <span className="mention-dot" style={{ background: p.color || '#d96f43' }} />
                    <span>{p.name}</span>
                    {p.type === 'ai' && <AIBadge />}
                  </button>
                ))}
              </div>
            )}
            <div className="composer-hint">Tap a message to capture it · mention Aimy to ask</div>
            <form
              className={`message-input ${dragOver ? 'drag-over' : ''} ${inputFocused || input.length > 0 || replyTo || editingMessage ? 'expanded' : 'collapsed'}`}
              onSubmit={handleSend}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {pendingAttachments.length > 0 && (
                <div className="attachment-preview-bar">
                  {pendingAttachments.map((att) => (
                    <div key={att.id} className="attachment-preview-item">
                      {att.isImage ? (
                        <img
                          src={att.url}
                          alt={att.originalName}
                          className="attachment-preview-thumb"
                        />
                      ) : (
                        <span className="attachment-preview-name">{att.originalName}</span>
                      )}
                      <button
                        type="button"
                        className="attachment-preview-remove"
                        onClick={() =>
                          setPendingAttachments((prev) => prev.filter((a) => a.id !== att.id))
                        }
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {dragOver && <div className="drag-overlay">Drop files here</div>}
              <div className="input-row">
                {/* The textarea renders its own visible text so the caret and
                    the glyphs are always the same element — they can never drift
                    apart (a transparent-text-over-coloured-mirror trick drifted
                    whenever the two layers resolved different fonts). Mentions
                    are coloured in the sent message bubbles, not while typing. */}
                <div className="composer-editor">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    placeholder={
                      editingMessage
                        ? 'Edit your message...'
                        : replyTo
                          ? `Reply to ${replyTo.senderName}...`
                          : 'Message… (@Aimy to ask)'
                    }
                    rows={1}
                    disabled={!connected}
                  />
                  {/* Attach + send sit inside the text box, pinned bottom-right. */}
                  <div className="composer-actions">
                    <button
                      type="button"
                      className="btn-attach"
                      onClick={() => fileInputRef.current?.click()}
                      title="Attach a file or image (max 10MB; images are resized automatically)"
                      disabled={uploading}
                      aria-label="Attach a file or image (max 10MB)"
                    >
                      {uploading ? '...' : <IconPaperclip size={18} />}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="file-input-hidden"
                      multiple
                      onChange={(e) => {
                        if (e.target.files.length > 0) {
                          handleFilesSelected(Array.from(e.target.files));
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      type="submit"
                      className="btn-send"
                      title={editingMessage ? 'Save' : 'Send'}
                      aria-label={editingMessage ? 'Save' : 'Send'}
                      disabled={(!input.trim() && pendingAttachments.length === 0) || !connected}
                    >
                      <IconSend size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar backdrop for mobile/tablet */}
        {(sidebarOpen || rightPanelOpen || repliesRootId) && (
          <div
            className="sidebar-backdrop"
            onClick={() => {
              setSidebarOpen(false);
              setRightPanelOpen(false);
              setRepliesRootId(null);
            }}
          />
        )}

        {/* Left Sidebar - Participants */}
        {sidebarOpen && (
          <ParticipantsSidebar
            participants={participants}
            aiParticipants={room.aiParticipants || []}
            typingUsers={typingUsers}
            aiTyping={aiTyping}
            onlineUsers={onlineUsers || {}}
            aiPrefs={aiPrefs}
            onToggleMuteAI={onToggleMuteAI}
            onSetPrimaryAI={onSetPrimaryAI}
          />
        )}

        {/* Right Panel - Artifacts, Todos & Decisions */}
        {rightPanelOpen && (
          <RightPanel
            artifacts={artifacts}
            todos={todos}
            decisions={decisions || []}
            shortLists={shortLists || []}
            siblingThreadSummary={siblingThreadSummary}
            decisionSources={decisionSources}
            onAddArtifact={onAddArtifact}
            onRemoveArtifact={onRemoveArtifact}
            onUpdateArtifact={onUpdateArtifact}
            onGetArtifactVersions={onGetArtifactVersions}
            onAddTodo={onAddTodo}
            onToggleTodo={onToggleTodo}
            onRemoveTodo={onRemoveTodo}
            onLogDecision={onLogDecision}
            onRemoveDecision={onRemoveDecision}
            onUpdateDecisionStatus={onUpdateDecisionStatus}
            onAddShortList={onAddShortList}
            onAddShortListItem={onAddShortListItem}
            onRemoveShortListItem={onRemoveShortListItem}
            onToggleShortListItemPin={onToggleShortListItemPin}
            onRemoveShortList={onRemoveShortList}
            onJumpToMessage={jumpToMessage}
            onVotePoll={onVotePoll}
            onClosePoll={onClosePoll}
            onToggleChecklistItem={onToggleChecklistItem}
            currentUserId={user.id}
            workspaceId={workspaceId}
          />
        )}

        {/* Replies Panel - inline replies */}
        {repliesRootId && (
          <RepliesPanel
            rootId={repliesRootId}
            messages={messages}
            participants={allParticipants}
            user={user}
            onClose={() => setRepliesRootId(null)}
            onReply={(msg) => {
              handleReply(msg);
              setRepliesRootId(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Replies panel shows a root message and all its replies (direct + nested).
 * Uses the client-side messages array — no extra socket calls needed.
 */
function RepliesPanel({ rootId, messages, participants, user, onClose, onReply }) {
  const root = messages.find((m) => m.id === rootId);
  if (!root) return null;

  // Collect the root and every descendant reply via BFS through replyTo chains
  const replyIds = new Set([rootId]);
  const queue = [rootId];
  while (queue.length > 0) {
    const parentId = queue.shift();
    for (const m of messages) {
      if (m.replyTo === parentId && !replyIds.has(m.id)) {
        replyIds.add(m.id);
        queue.push(m.id);
      }
    }
  }
  const replyMessages = messages
    .filter((m) => replyIds.has(m.id))
    .sort((a, b) => (a.timestamp > b.timestamp ? 1 : -1));

  const replyCount = replyMessages.length - 1;

  return (
    <aside className="replies-panel">
      <div className="replies-panel-header">
        <div>
          <strong>Replies</strong>
          <span className="replies-count">
            {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
          </span>
        </div>
        <button className="btn-close-search" onClick={onClose}>
          x
        </button>
      </div>
      <div className="replies-panel-list">
        {replyMessages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isOwn={msg.senderId === user.id}
            participants={participants}
            currentUserId={user.id}
            onReply={onReply}
          />
        ))}
      </div>
    </aside>
  );
}

export default ChatThread;
